import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Rocket, Loader2, Save, LayoutDashboard, Tag, FileText, Info } from "lucide-react";
import { apiGet } from "@/api";
import { useAuth } from "@/context/useAuth";

interface PublishAppModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    defaultName: string;
    defaultDescription?: string;
    initialContentTypeId?: number;
    initialVersionLabel?: string;
    externalVersionError?: string | null;
    onPublish: (
        name: string,
        description: string,
        contentTypeId: number,
        sharedWith: string[],
        folderName: string,
        status?: string,
        overwrite?: boolean,
        linkedDashboardId?: string,
        versionLabel?: string
    ) => Promise<void>;
}

export default function PublishAppModal({
    open,
    onOpenChange,
    defaultName,
    defaultDescription = "",
    initialContentTypeId = 4,
    initialVersionLabel = "",
    externalVersionError,
    onPublish,
}: PublishAppModalProps) {
    const [name, setName] = useState(defaultName);
    const [versionLabel, setVersionLabel] = useState(initialVersionLabel);
    // versionDescription is saved to FlowVersion.description — shown in bundle version picker
    const [versionDescription, setVersionDescription] = useState("");
    const [description, setDescription] = useState(defaultDescription);
    const [contentTypeId, setContentTypeId] = useState<number>(initialContentTypeId);
    const [shareMode, setShareMode] = useState("me");
    const [folderName, setFolderName] = useState("");
    const [status, setStatus] = useState("Published");
    const [linkedDashboardId, setLinkedDashboardId] = useState<string>("__none__");
    const [dashboards, setDashboards] = useState<{ id: string; name: string }[]>([]);
    const [contentTypes, setContentTypes] = useState<{ contentTypeId: number; contentTypeName: string }[]>([]);
    const [isPublishing, setIsPublishing] = useState(false);
    const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
    const [versionError, setVersionError] = useState<string | null>(null);

    const isDashboard = initialContentTypeId === 3;

    // Roles that can share content with all users:
    // r1 = Platform Super Admin, r2 = Platform Admin, r3 = Partner Admin, r4 = Tenant Admin
    const { user } = useAuth();
    const canShareWithAll = user?.roleId && ['r1', 'r2', 'r3', 'r4'].includes(user.roleId);

    useEffect(() => {
        if (externalVersionError) setVersionError(externalVersionError);
    }, [externalVersionError]);

    useEffect(() => {
        if (open) {
            setName(defaultName);
            setVersionLabel(initialVersionLabel || "");
            setVersionDescription("");
            setDescription(defaultDescription || "");
            setShareMode("me");
            setContentTypeId(initialContentTypeId ?? 4);
            setFolderName("");
            setStatus("Published");
            setLinkedDashboardId("__none__");
            setShowOverwriteConfirm(false);
            setVersionError(null);

            if (!isDashboard) {
                apiGet("/business-apps/contents?category=dashboard")
                    .then((res: any) => {
                        if (res && Array.isArray(res)) setDashboards(res);
                    })
                    .catch((err) => console.error("Failed to load dashboards for linking:", err));
            }

            apiGet("/content-types")
                .then((res: any) => {
                    if (res && Array.isArray(res)) {
                        if (isDashboard) {
                            setContentTypes(res.filter((ct: any) => ct.contentTypeId === 3));
                        } else {
                            const flowTypes = res
                                .filter((ct: any) => ![3, 5].includes(ct.contentTypeId))
                                .sort((a: any, b: any) => {
                                    const order: Record<number, number> = { 4: 0, 2: 1, 1: 2, 7: 3, 99: 4 };
                                    return (order[a.contentTypeId] ?? 9) - (order[b.contentTypeId] ?? 9);
                                });
                            setContentTypes(flowTypes);
                        }
                    }
                })
                .catch((err) => console.error("Failed to load content types:", err));
        }
    }, [open, defaultName, initialContentTypeId, initialVersionLabel]);

    const handleSubmit = async (overwrite = false) => {
        if (!name.trim()) return;
        setIsPublishing(true);
        setVersionError(null);
        try {
            const sharedWith = shareMode === "all" ? ["*"] : [];
            const resolvedDashId = linkedDashboardId === "__none__" ? undefined : linkedDashboardId;
            const resolvedVersion = versionLabel.trim() || undefined;
            // versionDescription overrides description if provided — it maps to FlowVersion.description
            // so it shows in the bundle assignment version picker
            const resolvedDescription = versionDescription.trim() || description;
            await onPublish(name, resolvedDescription, contentTypeId, sharedWith, folderName, status, overwrite, resolvedDashId, resolvedVersion);
            onOpenChange(false);
        } catch (error: any) {
            const msg = error.message || "";
            if (msg.includes("409")) {
                if (msg.toLowerCase().includes("version") || msg.toLowerCase().includes("label")) {
                    setVersionError(
                        `Version label "${versionLabel}" already exists for this ${isDashboard ? "dashboard" : "flow"}. Please choose a different label.`
                    );
                } else {
                    setShowOverwriteConfirm(true);
                }
            } else {
                console.error(error);
            }
        } finally {
            setIsPublishing(false);
        }
    };

    if (showOverwriteConfirm) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="bg-white">
                    <DialogHeader>
                        <DialogTitle className="text-red-900">App Already Exists</DialogTitle>
                        <DialogDescription>
                            An app with the name <strong>"{name}"</strong> already exists. Do you want to overwrite it?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowOverwriteConfirm(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={() => handleSubmit(true)} disabled={isPublishing}>
                            {isPublishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Yes, Overwrite
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[540px] bg-white">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {isDashboard ? (
                            <LayoutDashboard className="h-5 w-5 text-blue-600" />
                        ) : (
                            <Rocket className="h-5 w-5 text-purple-600" />
                        )}
                        {isDashboard ? "Publish Dashboard" : "Publish Flow"}
                    </DialogTitle>
                    <DialogDescription>
                        {isDashboard
                            ? "Save and publish this dashboard to the Content Library."
                            : "Save and publish this flow as an App, Project, or Dashboard."}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    {/* Type + Share */}
                    <div className={isDashboard ? "grid grid-cols-1 gap-4" : "grid grid-cols-2 gap-4"}>
                        {!isDashboard && (
                            <div className="grid gap-2">
                                <Label>Save As Type</Label>
                                <Select value={String(contentTypeId)} onValueChange={(v) => setContentTypeId(Number(v))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {contentTypes.length > 0
                                            ? contentTypes.map((ct) => (
                                                  <SelectItem key={ct.contentTypeId} value={String(ct.contentTypeId)}>
                                                      {ct.contentTypeName}
                                                  </SelectItem>
                                              ))
                                            : <SelectItem value="4">Workflow App</SelectItem>}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="grid gap-2">
                            <Label>Share With</Label>
                            <Select value={shareMode} onValueChange={setShareMode}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select visibility" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="me">Me Only (Private)</SelectItem>
                                    {canShareWithAll && (
                                        <SelectItem value="all">All Users (Tenant)</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Name */}
                    <div className="grid gap-2">
                        <Label htmlFor="pub-name">Name</Label>
                        <Input
                            id="pub-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Monthly Sales Processor"
                        />
                    </div>

                    {/* Version Label + Version Notes (side-by-side) */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="pub-version" className="flex items-center gap-1.5">
                                <Tag className="w-3.5 h-3.5 text-slate-400" />
                                Version Label
                                <span className="text-slate-400 text-xs font-normal">(optional)</span>
                            </Label>
                            <Input
                                id="pub-version"
                                value={versionLabel}
                                onChange={(e) => {
                                    setVersionLabel(e.target.value);
                                    setVersionError(null);
                                }}
                                placeholder="e.g. v2.0 or Sprint-3"
                                className={versionError ? "border-red-500 focus-visible:ring-red-400" : ""}
                            />
                            {versionError && (
                                <p className="text-xs text-red-600 flex items-center gap-1">⚠ {versionError}</p>
                            )}
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="pub-ver-desc" className="flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5 text-slate-400" />
                                Version Notes
                                <span className="text-slate-400 text-xs font-normal">(optional)</span>
                            </Label>
                            <Input
                                id="pub-ver-desc"
                                value={versionDescription}
                                onChange={(e) => setVersionDescription(e.target.value)}
                                placeholder="What changed in this version?"
                            />
                            <p className="text-[10px] text-slate-400 flex items-center gap-0.5 -mt-1">
                                <Info className="w-2.5 h-2.5" /> Shown in bundle version picker
                            </p>
                        </div>
                    </div>

                    {/* Status — hidden for dashboard publish (always published) */}
                    {!isDashboard && (
                    <div className="grid gap-2">
                        <Label>Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Published">Published</SelectItem>
                                <SelectItem value="Inactive">Draft</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    )}

                    {/* App Description */}
                    <div className="grid gap-2">
                        <Label htmlFor="desc">App Description</Label>
                        <Textarea
                            id="desc"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe what this app does..."
                            className="h-16"
                        />
                    </div>

                    {/* Dashboard Link */}
                    {(contentTypeId === 4 || contentTypeId === 1 || contentTypeId === 7) && (
                        <div className="grid gap-2 border-t pt-4 mt-2">
                            <Label className="flex items-center gap-2">
                                <LayoutDashboard className="w-4 h-4 text-slate-500" />
                                Link Results Dashboard (Optional)
                            </Label>
                            <p className="text-xs text-slate-500">
                                If selected, users will see a "View Results" button after running this app.
                            </p>
                            <Select value={linkedDashboardId} onValueChange={setLinkedDashboardId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a dashboard..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">None</SelectItem>
                                    {dashboards.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>
                                            {d.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPublishing}>
                        Cancel
                    </Button>
                    <Button
                        id="publish-app-confirm"
                        onClick={() => handleSubmit(false)}
                        disabled={isPublishing || !name.trim()}
                        className="bg-purple-600 hover:bg-purple-700"
                    >
                        {isPublishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Publish
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
